import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import LoadingOverlay from "react-loading-overlay-ts";
import { useGeolocated } from "react-geolocated";
import axios from "axios";
import L from "leaflet";
import pako from "pako";
import qs from "query-string";
import toast, { Toaster } from "react-hot-toast";

import Spinner from "./components/Spinner";
import NavAccess from "./components/NavAccess";
import Header from "./components/Header";
import PostajaStranica from "./components/PostajaStranica";
import Obveznici from "./components/Obveznici";
import Kontakt from "./components/Kontakt";
import Info from "./components/Info";
import Projekt from "./components/Projekt";
import Prijava from "./components/Prijava";
import Registracija from "./components/Registracija";
import Naslovnica from "./components/Naslovnica";
import Footer from "./components/Footer";
import ResetiranjeZaporke from "./components/ResetiranjeZaporke";
import PrijavaGreske2 from "./components/PrijavaGreske2";
import Odjava from "./components/Odjava";
import ErrorComponent from "./components/ErrorComponent";
import Privatnost from "./components/Privatnost";

export const DataContext = React.createContext();
export const CoordsContext = React.createContext({
  coords: null,
  setCoords: () => {}
});
export const MapContext = React.createContext();
export const AuthContext = React.createContext({
  auth: null,
  login: () => {},
  signOut: () => {}
});
export const LoadingContext = React.createContext({
  loading: null,
  setLoading: () => {}
});

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    coords: geoCoords,
    isGeolocationAvailable,
    isGeolocationEnabled
  } = useGeolocated({
    positionOptions: { enableHighAccuracy: true },
    userDecisionTimeout: 30000
  });

  const [data, setData] = useState();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(true);

  const [coords, setCoords] = useState({
    latitude: 45.813177,
    longitude: 15.977048
  });

  useEffect(() => {
    if (isGeolocationAvailable && isGeolocationEnabled && geoCoords) {
      setCoords({
        latitude: geoCoords.latitude,
        longitude: geoCoords.longitude
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoCoords]);

  const [currentPath, setCurrentPath] = useState(location.pathname);
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  const [map, setMap] = useState();
  useEffect(() => {
    // Hidden Leaflet map za routing-machine pozive izvan MapContainer-a
    // (npr. getDistanceAndTimeOfTwoLocations u PostajaStranica.js)
    const el = document.getElementById("hidden-map");
    if (el && !el._leaflet_id) {
      setMap(L.map("hidden-map").setView([45.1, 15.2], 7));
    }
  }, []);

  const [auth, setAuth] = useState(null);
  const login = (username, token) => {
    localStorage.setItem("username", username);
    localStorage.setItem("token", token);
    setAuth({ username: username, token: token });
    navigate("/");
  };
  const signOut = () => {
    localStorage.removeItem("username");
    localStorage.removeItem("token");
    setAuth(null);
    navigate("/");
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios
        .get(`${import.meta.env.VITE_BACKEND_API_URL}/check`, {
          headers: { Authorization: `JWT ${token}` }
        })
        .then(() => login(localStorage.getItem("username"), token))
        .catch(() => signOut());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const url = import.meta.env.VITE_DATA_URL;
    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buf) => {
        let text;
        if (url.endsWith(".gz")) {
          text = new TextDecoder().decode(pako.ungzip(new Uint8Array(buf)));
        } else {
          text = new TextDecoder().decode(buf);
        }
        const parsed = processResponseData(JSON.parse(text));
        setData(parsed);
        setLoading2(false);
      })
      .catch((e) => {
        console.error(e);
        setError(true);
        setLoading2(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlParams = qs.parse(location.search, { ignoreQueryPrefix: true });
    if (location.pathname !== "/" || !urlParams.token) return;
    axios
      .put(
        `${import.meta.env.VITE_BACKEND_API_URL}/confirm/${urlParams.token}`
      )
      .then((response) => {
        if (response.data.ok) {
          toast.success("Uspješno se potvrdili registraciju.");
        } else {
          toast(response.data.msg);
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          `Dogodila se greška potvrde registracije. Probajte ponovno za par minuta. U slučaju da i dalje imate problema, javite nam na e-adresu ${import.meta.env.VITE_EMAIL}.`
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading2) return <Spinner />;
  if (error)
    return (
      <ErrorComponent title="Greška prilikom dohvaćanja podataka potrebnih za rad aplikacije." />
    );

  return (
    <DataContext.Provider value={data}>
      <AuthContext.Provider value={{ auth, login, signOut }}>
        <CoordsContext.Provider value={{ coords, setCoords }}>
          <MapContext.Provider value={map}>
            <LoadingContext.Provider value={{ loading, setLoading }}>
              <LoadingOverlay active={loading} text="">
                <Toaster position="top-right" />
                <noscript>You need to enable JavaScript to run this app.</noscript>
                <NavAccess />
                <Header
                  currentPath={currentPath}
                  userLogged={auth ? true : false}
                  username={auth ? auth.username : null}
                />
                <Routes>
                  {data.postajas.map((postaja) => (
                    <Route
                      key={postaja.id}
                      path={postaja.url}
                      element={<PostajaStranica id={postaja.id} postaja={postaja} />}
                    />
                  ))}
                  <Route path="/obveznici" element={<Obveznici data={data} />} />
                  <Route path="/kontakt" element={<Kontakt />} />
                  <Route path="/info" element={<Info />} />
                  <Route path="/projekt" element={<Projekt />} />
                  {!auth && <Route path="/prijava" element={<Prijava />} />}
                  {!auth && (
                    <Route path="/registracija" element={<Registracija />} />
                  )}
                  {auth && <Route path="/odjava" element={<Odjava />} />}
                  <Route path="/" element={<Naslovnica coords={coords} />} />
                  <Route
                    path="/resetiranje-lozinke"
                    element={<ResetiranjeZaporke />}
                  />
                  {auth && (
                    <Route path="/prijava-greske" element={<PrijavaGreske2 />} />
                  )}
                  <Route path="/privatnost" element={<Privatnost />} />
                </Routes>
                <Footer />
              </LoadingOverlay>
            </LoadingContext.Provider>
          </MapContext.Provider>
        </CoordsContext.Provider>
      </AuthContext.Provider>
    </DataContext.Provider>
  );

  function processResponseData(responseData) {
    const data = {};
    const arrayToObject = (array, property) => {
      const keyValueObj = {};
      array.forEach((item) => (keyValueObj[item[property]] = item));
      return keyValueObj;
    };
    Object.entries(responseData).forEach(([k, v]) => {
      data[k] = v;
      data[k + "_obj"] = arrayToObject(v, "id");
    });

    const tipoviGoriva = [
      "Benzinska goriva",
      "Dizelska goriva",
      "Autoplin",
      "Plinsko ulje"
    ];
    data.tip_gorivas = data.tip_gorivas.filter((tip_goriva) =>
      tipoviGoriva.includes(tip_goriva.tip_goriva)
    );
    data.opcina_grads.forEach((v) => {
      v.zupanija = data.zupanijas_obj[v.zupanija_id];
    });
    data.naseljes = data.naseljes.filter((v) => v.lat && v.lng);
    data.naseljes.forEach((v) => {
      v.opcina_grad = data.opcina_grads_obj[v.opcina_grad_id];
    });
    data.suggestions = [];
    data.naseljes.forEach((v) => {
      const suggestion = {
        id: v.id,
        name:
          v.ime +
          ", " +
          v.opcina_grad.ime_jedinice +
          ", " +
          v.opcina_grad.zupanija.naziv,
        lat: v.lat,
        lng: v.lng,
        name2: v.url ? v.url.split("-").join(" ") : v.url
      };
      data.suggestions.push(suggestion);
    });
    data.suggestions_obj = arrayToObject(data.suggestions, "id");
    data.vrsta_gorivas.forEach((v) => {
      v.tip_goriva = data.tip_gorivas_obj[v.tip_goriva_id];
    });
    const tipGorivaIds = data.tip_gorivas.map((tipGoriva) => tipGoriva.id);
    data.vrsta_gorivas = data.vrsta_gorivas.filter(
      (v) => v.tip_goriva !== undefined && tipGorivaIds.includes(v.tip_goriva.id)
    );
    data.gorivos.forEach((v) => {
      v.vrsta_goriva = data.vrsta_gorivas_obj[v.vrsta_goriva_id];
    });
    const vrstaGorivaIds = data.vrsta_gorivas.map(
      (vrstaGoriva) => vrstaGoriva.id
    );
    data.gorivos = data.gorivos.filter(
      (v) =>
        v.vrsta_goriva !== undefined &&
        vrstaGorivaIds.includes(v.vrsta_goriva.id)
    );
    const gorivoIds = data.gorivos.map((gorivo) => gorivo.id);
    data.postajas = data.postajas.filter((v) => {
      return !(
        !v.lat ||
        isNaN(+v.lat) ||
        parseFloat(v.lat.replace(",", ".")) < -180 ||
        parseFloat(v.lat.replace(",", ".")) > 180 ||
        !v.long ||
        isNaN(+v.long) ||
        parseFloat(v.long.replace(",", ".")) < -90 ||
        parseFloat(v.long.replace(",", ".")) > 90
      );
    });
    data.postajas.forEach((postaja) => {
      postaja.lat = parseFloat(postaja.lat.replace(",", "."));
      postaja.long = parseFloat(postaja.long.replace(",", "."));

      postaja.obveznik = data.obvezniks_obj[postaja.obveznik_id];
      postaja.opcijas = postaja.opcije;
      postaja.opcijas_obj = arrayToObject(postaja.opcije, "id");
      postaja.opcijas.forEach((v) => {
        v.opcija = data.opcijas_obj[v.opcija_id];
      });
      postaja.opcijas = postaja.opcijas.filter((v) => v.opcija !== undefined);
      postaja.cjeniks = postaja.cjenici;
      postaja.cjeniks_obj = arrayToObject(postaja.cjenici, "id");
      postaja.cjeniks.forEach((v) => {
        v.gorivo = data.gorivos_obj[v.gorivo_id];
      });
      postaja.cjeniks = postaja.cjeniks.filter(
        (v) => v.gorivo !== undefined && gorivoIds.includes(v.gorivo.id)
      );
      postaja.radno_vrijemes = postaja.radnaVremena;
      postaja.radno_vrijemes_obj = arrayToObject(postaja.radnaVremena, "id");
      postaja.radno_vrijemes.forEach((v) => {
        v.vrsta_dana = data.vrsta_danas_obj[v.vrsta_dana_id];
      });
      postaja.radno_vrijemes = postaja.radno_vrijemes.filter(
        (v) => v.vrsta_dana !== undefined
      );
      postaja.obavijests = postaja.obavijesti;
      postaja.obavijests_obj = arrayToObject(postaja.obavijesti, "id");
      postaja.url = `/${postaja.url}`;
    });
    data.postajas = data.postajas.filter((v) => {
      return !(v.cjeniks.length === 0 || v.radno_vrijemes.length === 0);
    });
    return data;
  }
}

export default App;
